import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { extractError } from "@/api/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { LifeBuoy, Plus, Trash2, Pencil, RotateCcw, GraduationCap, BookOpen, HelpCircle, ImagePlus, Video, Loader2 } from "lucide-react";
import { toast } from "sonner";
import AuthImage from "@/components/AuthImage";
import AuthVideo from "@/components/AuthVideo";

const SECTION_META = {
  training: { label: "Formations", icon: GraduationCap },
  guide: { label: "Notices", icon: BookOpen },
  faq: { label: "FAQ", icon: HelpCircle },
};

const emptyArticle = { profile: "", section: "guide", title: "", content: "", order: 0 };

// Admin sub-module: manage the Centre d'aide content (FAQ/notices/formations), grouped by
// profile (Gestionnaires / Prestataires externes / Locataires / Finance). Mirrors PickListsSection.
export default function HelpContentSection() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["help-content", "admin"], queryFn: () => api.get("/help-content").then((r) => r.data) });
  const [activeProfile, setActiveProfile] = useState(null);
  const [editing, setEditing] = useState(null); // article or null
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyArticle);

  const profiles = data?.profiles || {};
  useEffect(() => { if (!activeProfile && Object.keys(profiles).length) setActiveProfile(Object.keys(profiles)[0]); }, [profiles, activeProfile]);

  const articles = (data?.articles || []).filter((a) => a.profile === activeProfile);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["help-content", "admin"] });
  const upsert = useMutation({
    mutationFn: (body) => (editing ? api.patch(`/help-content/${editing.id}`, body) : api.post("/help-content", { ...body, profile: activeProfile })),
    onSuccess: () => { invalidate(); toast.success(editing ? "Article mis à jour" : "Article créé"); setDialogOpen(false); },
    onError: (e) => toast.error(extractError(e, "Échec de l'enregistrement")),
  });
  const remove = useMutation({
    mutationFn: (id) => api.delete(`/help-content/${id}`),
    onSuccess: () => { invalidate(); toast.success("Article supprimé"); },
    onError: (e) => toast.error(extractError(e, "Échec de la suppression")),
  });
  const reset = useMutation({
    mutationFn: () => api.post(`/help-content/reset?profile=${activeProfile}`),
    onSuccess: (res) => { invalidate(); toast.success(`${res.data.restored} article(s) restauré(s) pour ce profil`); },
    onError: (e) => toast.error(extractError(e, "Échec")),
  });

  const openCreate = (section) => { setEditing(null); setForm({ ...emptyArticle, profile: activeProfile, section }); setDialogOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ profile: a.profile, section: a.section, title: a.title, content: a.content, order: a.order || 0 }); setDialogOpen(true); };

  const handleSave = () => {
    if (!form.title.trim() || !form.content.trim()) { toast.error("Titre et contenu sont obligatoires"); return; }
    upsert.mutate({ section: form.section, title: form.title.trim(), content: form.content.trim(), order: Number(form.order) || 0 });
  };

  return (
    <div className="grid md:grid-cols-[240px_1fr] gap-4">
      <Card className="p-2 border-slate-200 h-fit">
        {Object.entries(profiles).map(([key, label]) => (
          <button
            key={key}
            data-testid={`help-profile-${key}`}
            onClick={() => setActiveProfile(key)}
            className={`w-full text-left px-3 py-2 rounded-md text-sm mb-1 transition ${activeProfile === key ? "bg-[#023E73] text-white font-semibold" : "hover:bg-slate-100 text-slate-700"}`}
          >
            {label}
          </button>
        ))}
      </Card>

      <div className="space-y-5">
        {activeProfile && (
          <div className="flex items-center justify-between">
            <div className="font-display font-semibold text-lg flex items-center gap-2"><LifeBuoy size={16} className="text-[#023E73]" />{profiles[activeProfile]}</div>
            <Button data-testid="help-reset-profile" variant="outline" size="sm" onClick={() => reset.mutate()} disabled={reset.isPending}>
              <RotateCcw size={13} className="mr-1.5" /> Contenu par défaut
            </Button>
          </div>
        )}

        {["training", "guide", "faq"].map((section) => {
          const { label, icon: Icon } = SECTION_META[section];
          const items = articles.filter((a) => a.section === section).sort((a, b) => (a.order || 0) - (b.order || 0));
          return (
            <Card key={section} className="border-slate-200 overflow-hidden">
              <div className="px-4 py-2.5 border-b border-slate-200 flex items-center justify-between bg-slate-50">
                <div className="text-sm font-semibold flex items-center gap-2"><Icon size={14} className="text-[#023E73]" /> {label} ({items.length})</div>
                <Button data-testid={`help-add-${section}`} variant="ghost" size="sm" onClick={() => openCreate(section)}><Plus size={13} className="mr-1" /> Ajouter</Button>
              </div>
              <div className="divide-y divide-slate-100">
                {items.length === 0 && <div className="px-4 py-3 text-xs text-slate-400">Aucun article.</div>}
                {items.map((a) => (
                  <div key={a.id} data-testid={`help-item-${a.id}`} className="px-4 py-2.5 flex items-center justify-between gap-3">
                    <span className="text-sm text-slate-800 truncate flex-1">{a.title}</span>
                    <div className="flex gap-1 flex-shrink-0">
                      <button data-testid={`help-edit-${a.id}`} onClick={() => openEdit(a)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500"><Pencil size={13} /></button>
                      <button data-testid={`help-delete-${a.id}`} onClick={() => { if (window.confirm(`Supprimer « ${a.title} » ?`)) remove.mutate(a.id); }} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 size={13} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifier l'article" : "Nouvel article"}</DialogTitle>
            <DialogDescription>Ce contenu est visible par les utilisateurs du profil « {profiles[activeProfile]} » dans le Centre d'aide.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Titre</Label><Input data-testid="help-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div><Label>Contenu</Label><Textarea data-testid="help-form-content" rows={10} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} /></div>
            <div className="w-32"><Label>Ordre d'affichage</Label><Input data-testid="help-form-order" type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} /></div>
            {editing && <HelpArticleMedia article={editing} onChanged={invalidate} />}
            {!editing && <p className="text-xs text-slate-400 italic">Enregistrez d'abord l'article pour pouvoir y ajouter des captures d'écran ou des vidéos.</p>}
          </div>
          <DialogFooter>
            <Button data-testid="help-form-submit" className="text-white" style={{ background: "#023E73" }} onClick={handleSave} disabled={upsert.isPending}>
              {editing ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Screenshot(s) + short video(s) upload/removal for a single article, shown inside the edit dialog.
function HelpArticleMedia({ article, onChanged }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(null); // "image" | "video" | null
  const current = (qc.getQueryData(["help-content", "admin"])?.articles || []).find((a) => a.id === article.id) || article;

  const handleUpload = async (kind, files) => {
    if (!files || files.length === 0) return;
    setBusy(kind);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/help-content/${article.id}/media?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      onChanged();
      toast.success(kind === "image" ? "Capture d'écran ajoutée" : "Vidéo ajoutée");
    } catch (e) {
      toast.error(extractError(e, "Échec de l'envoi du média"));
    } finally {
      setBusy(null);
    }
  };

  const removeMedia = async (kind, mediaId) => {
    try {
      await api.delete(`/help-content/${article.id}/media/${mediaId}?kind=${kind}`);
      onChanged();
      toast.success("Média supprimé");
    } catch (e) {
      toast.error(extractError(e, "Échec de la suppression"));
    }
  };

  return (
    <div className="border-t border-slate-100 pt-3 space-y-3">
      <div>
        <div className="flex items-center justify-between">
          <Label>Captures d'écran</Label>
          <label className="cursor-pointer">
            <input data-testid="help-image-input" type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleUpload("image", Array.from(e.target.files || []))} />
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#023E73] border border-slate-200 rounded-md px-2 py-1 hover:bg-slate-50">
              {busy === "image" ? <Loader2 size={12} className="animate-spin" /> : <ImagePlus size={12} />} Ajouter
            </span>
          </label>
        </div>
        {(current.images || []).length > 0 && (
          <div className="grid grid-cols-4 gap-2 mt-2">
            {current.images.map((img) => (
              <div key={img.id} data-testid={`help-image-${img.id}`} className="relative aspect-square rounded-md border border-slate-200 overflow-hidden bg-slate-50 group">
                <AuthImage src={img.url} alt={img.name} className="w-full h-full object-cover" />
                <button data-testid={`help-image-delete-${img.id}`} onClick={() => removeMedia("image", img.id)} className="absolute top-1 right-1 p-1 rounded bg-white/90 text-red-600 opacity-0 group-hover:opacity-100 transition shadow">
                  <Trash2 size={11} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <div className="flex items-center justify-between">
          <Label>Vidéos de formation</Label>
          <label className="cursor-pointer">
            <input data-testid="help-video-input" type="file" accept="video/mp4,video/webm,video/quicktime" multiple className="hidden" onChange={(e) => handleUpload("video", Array.from(e.target.files || []))} />
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#023E73] border border-slate-200 rounded-md px-2 py-1 hover:bg-slate-50">
              {busy === "video" ? <Loader2 size={12} className="animate-spin" /> : <Video size={12} />} Ajouter
            </span>
          </label>
        </div>
        {(current.videos || []).length > 0 && (
          <div className="space-y-2 mt-2">
            {current.videos.map((v) => (
              <div key={v.id} data-testid={`help-video-${v.id}`} className="relative">
                <AuthVideo src={v.url} className="aspect-video" />
                <button data-testid={`help-video-delete-${v.id}`} onClick={() => removeMedia("video", v.id)} className="absolute top-2 right-2 p-1.5 rounded bg-white/90 text-red-600 shadow">
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      <p className="text-[11px] text-slate-400">Images : JPEG/PNG/WEBP, 8 Mo max. Vidéos : MP4/WEBM/MOV, 80 Mo max.</p>
    </div>
  );
}
